import type { FabricEditor } from 'modules/fabric-editor';
import type { LayoutScopeKey, MatchLayoutSettings } from 'modules/manager/domain/match-layout';
import type { ResolverContext } from 'modules/manager/domain/placeholder';
import type { TemplateType } from 'modules/manager/domain/template-type';
import { applyLayoutBackgrounds } from './apply-layout-backgrounds';
import { centerScopedBundles } from './center-scoped-bundles';
import { ensureMandatoryLogo } from './ensure-mandatory-logo';
import { filterScopedObjects } from './filter-scoped-objects';
import { substitutePlaceholders } from './substitute-placeholders';

export interface RenderPipelineConfig {
  /** Positional background URLs for layout-block regions (`bg1..bgN`). */
  bgUrls?: (string | undefined)[];
  /** Active template type — placeholders whose key isn't allowed for it are dropped. */
  templateType?: TemplateType;
  /** Active template subtypes, forwarded into the resolver context. */
  templateSubtypes?: readonly string[];
  /**
   * Slot layout/centering. Works for every repeated-slot mode — matches
   * (bundles addressed by `matchNumber`) and standings (rows addressed by
   * `teamNumber`) — the scope key is derived from `ctx.mode` by `scopeOf`.
   * Omit to skip centering.
   */
  layout?: { settings: MatchLayoutSettings | undefined; authoredObjects: readonly unknown[] | undefined };
}

/**
 * Scope key + slot count for a render context. One switch rather than two
 * parallel ternaries: the key and the count can no longer drift apart, and a new
 * `ResolverContext` variant is a compile error instead of silently inheriting
 * the match branch's `matchNumber` / `ctx.matches`.
 */
function scopeOf(ctx: ResolverContext): { scopeKey: LayoutScopeKey; count: number } {
  switch (ctx.mode) {
    case 'standings':
      return { scopeKey: 'teamNumber', count: ctx.standingTeams.length };
    case 'player_stats':
      return { scopeKey: 'playerNumber', count: ctx.rankedPlayers.length };
    case 'match':
      return { scopeKey: 'matchNumber', count: ctx.matches?.length ?? 0 };
    default: {
      const never: never = ctx;
      return never;
    }
  }
}

/**
 * The single shared headless-render pipeline, driven by both the match render
 * page (`/manager/render`) and the standings render page
 * (`/manager/render-standings`). Mutates the editor's canvas in place; the
 * caller loads the template JSON before and exports the canvas after.
 *
 * The scope key and slot count are derived from the discriminated
 * `ResolverContext.mode` (see `scopeOf`), so a future render type only needs a
 * new `ResolverContext` variant plus its config.
 *
 * Step order: layout backgrounds → strip out-of-range slot objects → center
 * surviving bundles → substitute placeholders → ensure mandatory logo.
 * Centering runs before substitution so positions are computed against authored
 * placeholder sizes, not resolved-content sizes. The mandatory logo is ensured
 * last so it sits on top of the resolved content in the exported image.
 */
export async function runRenderPipeline(
  editor: FabricEditor,
  ctx: ResolverContext,
  config: RenderPipelineConfig = {},
): Promise<void> {
  const { scopeKey, count } = scopeOf(ctx);

  // Web fonts load asynchronously (WebFont.load in index.html). If a font a
  // template uses hasn't arrived yet, placeholder substitution below would
  // measure and lay out text against the fallback (Arial) and bake it into the
  // snapshot. Block until pending font loading settles first. No-op when the
  // font-loading plugin isn't installed.
  await editor.fonts?.whenReady();

  await applyLayoutBackgrounds(editor, config.bgUrls ?? []);
  await filterScopedObjects(editor, scopeKey, count);
  if (config.layout) {
    await centerScopedBundles(editor, scopeKey, count, config.layout.settings, config.layout.authoredObjects);
  }
  await substitutePlaceholders(editor, ctx, {
    templateType: config.templateType,
    templateSubtypes: config.templateSubtypes,
  });
  await ensureMandatoryLogo(editor);
  editor.canvas.requestRenderAll();
}
